import { useRef, useState } from "react";

import { useClickOutside, useDebounce } from "common/hooks";
import { cycleIndex } from "common/utils/arrays";
import LinkButton from "common/components/LinkButton";

import PillSearchInput from "client/common/components/inputs/PillSearchInput";

import { useSupportItemsQuery } from "../hooks";
import { catchallItems } from "../constants";
import { IListSupportItem } from "../types";

import styles from "../styles/SupportSearchInput.module.less";

type SupportSearchInputProps = {
  onSelect: (item: IListSupportItem) => void;
};

const SupportSearchInput = ({ onSelect }: SupportSearchInputProps) => {
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const enabled = search.length >= 3;

  const { data: items = [] } = useSupportItemsQuery({ q: debouncedSearch }, { enabled });

  const [isActive, setIsActive] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useClickOutside(searchRef, () => {
    setIsActive(false);
  });

  const focusInput = () => {
    inputRef.current?.select();
    setIsActive(true);
  };

  const blurInput = () => {
    inputRef.current?.blur();
    setIsActive(false);
    setActiveIndex(-1);
  };

  const cycleActiveIndex = (intendedIndex: number) => {
    setActiveIndex((i) => cycleIndex(0, items.length + 1, i + intendedIndex));
  };

  const handleSelect = (item: IListSupportItem) => {
    onSelect(item);
    blurInput();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        cycleActiveIndex(+1);
        return;
      case "ArrowUp":
        event.preventDefault();
        cycleActiveIndex(-1);
        return;
      case "Enter":
        if (activeIndex >= 0) {
          handleSelect(items[activeIndex]);
        }
        return;
      default:
        setActiveIndex(-1);
    }
  };

  const clear = () => {
    setSearch("");
    focusInput();
  };

  const showItems = enabled && isActive;

  return (
    <div className={styles.control} ref={searchRef}>
      <PillSearchInput
        value={search}
        placeholder="Search for FAQ's..."
        onChange={setSearch}
        onClear={clear}
        onKeyDown={handleKeyDown}
        onFocus={focusInput}
      />
      <div className={`${styles.items} ${isActive && showItems ? "" : styles.hidden}`}>
        {items.map((item, index) => (
          <LinkButton
            key={item.id}
            className={`${styles.item} ${activeIndex === index ? styles.activeItem : ""}`}
            onClick={() => handleSelect(item)}
          >
            <div className={styles.itemTitle}>{item.title}</div>
            <div className={styles.itemSection}>{item.support_section.title}</div>
          </LinkButton>
        ))}
        {catchallItems.map((item) => (
          <LinkButton
            className={`${styles.item} ${styles.catchall} ${
              activeIndex === items.length ? styles.activeItem : ""
            }`}
            key={item.id}
            onClick={() => handleSelect(item)}
          >
            {item.title}
          </LinkButton>
        ))}
      </div>
    </div>
  );
};

export default SupportSearchInput;
